import type { Interval, MediaItem, MediaKind } from '~/generated/graphql';
import { assert } from './assert';

/**
 * Kinds that play for a duration rather than being held for a fixed time. A
 * PowerPoint runs for as long as its converted slideshow does.
 */
export const VIDEO_LIKE_KINDS: readonly MediaKind[] = ['VIDEO', 'POWERPOINT'];

/**
 * Kinds whose originalMetadata is the `geometry`/`format`/`size` shape. A
 * PowerPoint's original file is the deck itself, described this way by
 * ReceivePptToVideoConvert, so its size and extension are the deck's rather
 * than the derived mp4's.
 */
const GEOMETRY_KINDS: readonly MediaKind[] = ['IMAGE', 'PDF', 'POWERPOINT'];

/**
 * Helper to return the duration of a video media item
 * @param mediaItem
 * @returns
 */
export function videoDuration(
  mediaItem: Pick<MediaItem, 'kind' | 'defaultDuration'>,
): Interval | undefined {
  assert(VIDEO_LIKE_KINDS.includes(mediaItem.kind));
  return mediaItem.defaultDuration ?? undefined;
}

/**
 * Helper function to return the dimensions for uploaded media.
 * @param mediaItem
 */
export function mediaDimensions(
  mediaItem: Pick<MediaItem, 'kind' | 'originalMetadata'>,
): [number, number] {
  return GEOMETRY_KINDS.includes(mediaItem.kind)
    ? imageDimensions(mediaItem)
    : videoDimensions(mediaItem);
}

type ImageMetadata = { geometry?: { width: number; height: number } } | undefined | null;

/**
 * Helper function to return the dimensions for an uploaded image.
 * @param mediaItem
 */
export function imageDimensions(
  mediaItem: Pick<MediaItem, 'kind' | 'originalMetadata'>,
): [number, number] {
  assert(GEOMETRY_KINDS.includes(mediaItem.kind));
  const metadata = (mediaItem.originalMetadata ?? {}) as ImageMetadata;
  const width = metadata?.geometry?.width;
  const height = metadata?.geometry?.height;
  return typeof width === 'number' && typeof height === 'number' ? [width, height] : [0, 0];
}

/**
 * Helper function to return the dimensions for an uploaded video.
 * @param mediaItem
 */
export function videoDimensions(
  mediaItem: Pick<MediaItem, 'kind' | 'originalMetadata'>,
): [number, number] {
  assert(mediaItem.kind === 'VIDEO', 'videoDimensions: mediaItem is not a video');

  const metadata = mediaItem.originalMetadata;

  if (metadata == null) return [0, 0];

  const videoFormat = (metadata as any)?.['ebucore:ebuCoreMain']?.['ebucore:coreMetadata']?.[0]?.[
    'ebucore:format'
  ]?.[0]?.['ebucore:videoFormat']?.[0];

  const width = parseInt(videoFormat['ebucore:width'][0]['#value']);
  const height = parseInt(videoFormat['ebucore:height'][0]['#value']);

  assert(typeof width === 'number' && typeof height === 'number');

  return [width, height];
}

/**
 * Helper function to return the media size for uploaded media.
 * @param mediaItem
 */
export function mediaSize(mediaItem: Pick<MediaItem, 'kind' | 'originalMetadata'>): number {
  return GEOMETRY_KINDS.includes(mediaItem.kind) ? imageSize(mediaItem) : videoSize(mediaItem);
}

/**
 * Helper function to return the size of an uploaded image.
 * @param mediaItem
 */
export function imageSize(mediaItem: Pick<MediaItem, 'kind' | 'originalMetadata'>): number {
  assert(GEOMETRY_KINDS.includes(mediaItem.kind));
  const metadata = (mediaItem.originalMetadata ?? {}) as Record<string, unknown>;
  const size = metadata.size;
  return typeof size === 'number' ? size : 0;
}

/**
 * Helper function to return the size of an uploaded video.
 * @param mediaItem
 */
export function videoSize(mediaItem: Pick<MediaItem, 'kind' | 'originalMetadata'>): number {
  assert(mediaItem.kind === 'VIDEO', 'videoSize: mediaItem is not a video');

  const metadata = mediaItem.originalMetadata;

  if (metadata == null) return 0;

  const size = (metadata as any)?.['ebucore:ebuCoreMain']?.['ebucore:coreMetadata']?.[0]?.[
    'ebucore:format'
  ]?.[0]?.['ebucore:fileSize']?.[0]?.['#value'];

  assert(typeof size === 'string');
  return parseInt(size);
}

/**
 * Helper function to return the media extension for uploaded media.
 * @param mediaItem
 */
export function mediaExtension(mediaItem: Pick<MediaItem, 'kind' | 'originalMetadata'>): string {
  const extension = GEOMETRY_KINDS.includes(mediaItem.kind)
    ? imageExtension(mediaItem)
    : videoExtension(mediaItem);

  // Metadata is absent until an item finishes processing, but the kind is known
  // from the moment it is uploaded, so fall back to that rather than to nothing.
  return extension || mediaItem.kind;
}

/**
 * Helper function to return the file extension for an uploaded video.
 * @param mediaItem
 */
export function videoExtension(mediaItem: Pick<MediaItem, 'kind' | 'originalMetadata'>): string {
  assert(mediaItem.kind === 'VIDEO', 'videoExtension: mediaItem is not a video');

  const metadata = mediaItem.originalMetadata;

  if (metadata == null) return '';

  const videoFileName = (metadata as any)?.['ebucore:ebuCoreMain']?.['ebucore:coreMetadata']?.[0]?.[
    'ebucore:format'
  ]?.[0]?.['ebucore:fileName']?.[0]?.['#value'];

  assert(typeof videoFileName === 'string');

  const ext = new URL(videoFileName, window.location.href).pathname.split('.').pop() ?? '';
  return ext.toUpperCase();
}

/**
 * Helper function to return the file extension for an uploaded image.
 * @param mediaItem
 */
export function imageExtension(mediaItem: Pick<MediaItem, 'kind' | 'originalMetadata'>): string {
  assert(GEOMETRY_KINDS.includes(mediaItem.kind));
  const metadata = (mediaItem.originalMetadata ?? {}) as Record<string, unknown>;
  // Metadata is absent until an item finishes processing, so the type is
  // checked; coercing a missing format would render the text "UNDEFINED".
  return typeof metadata.format === 'string' ? metadata.format.toUpperCase() : '';
}
